import axios, { isAxiosError } from 'axios'
import { envVars } from '@/env-vars'
import {
	LOCAL_STORAGE_ACCESS_TOKEN,
	LOCAL_STORAGE_REFRESH_TOKEN
} from '@/features/auth/constants.ts'
import { useAuthStore } from '@/features/auth/store/store.ts'
import type { CreateClientConfig } from '@/sdk/gen/client.gen'

/**
 * Runtime configuration for the generated SDK client.
 *
 * The generator wires this in via `runtimeConfigPath` (see
 * openapi-ts.config.js): `client.gen.ts` imports `createClientConfig` and calls
 * it when the client is constructed. That means configuration is applied before
 * any SDK call can possibly run, with no "remember to bootstrap first" ordering
 * hazard, and without editing a generated file.
 *
 * The axios instance is built here rather than taken from the generator's
 * default so the 401 handling can live on it. That behavior is a straight port
 * of `axios-instance-node.ts`, the instance Marquee used before the SDK: same
 * backend, same bearer token, same handling. It is deliberately NOT the
 * refresh-and-retry flow from `axios-instance.ts` (the legacy vision-tool API);
 * Marquee never had that, and this migration does not change runtime behavior.
 */
const instance = axios.create()

instance.interceptors.response.use(
	(response) => response,
	(error: unknown) => {
		if (isAxiosError(error) && error.response?.status === 401) {
			localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
			localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN)
			useAuthStore.getState().reset()
		}

		return Promise.reject(error)
	}
)

export const createClientConfig: CreateClientConfig = (config) => ({
	...config,
	axios: instance,
	// Without this the client keeps the generator's default, which is the spec's
	// dev-only `servers` entry (http://localhost:80).
	baseURL: envVars.VITE_API_ENDPOINT_NODE,
	headers: {
		Accept: 'application/json',
		'X-Requested-With': 'XMLHttpRequest'
	},
	/**
	 * Read per request rather than captured once: the token changes on
	 * login/logout and the client outlives both. The SDK's `getAuthToken` adds
	 * the `Bearer ` prefix itself, so this returns the raw token.
	 */
	auth: () => localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN) ?? undefined
})
